import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
    ChevronRight, MapPin, Calendar, DollarSign,
    Clock, Users, Star, Compass, ArrowRight, Loader2, X, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FILTER_TABS = [
    { id: 'all', label: 'All Experiences' },
    { id: 'budget', label: 'By Budget', icon: DollarSign },
    { id: 'days', label: 'By Duration', icon: Clock },
    { id: 'destination', label: 'By Destination', icon: MapPin }
];

const BUDGET_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'budget', label: 'Budget (Under $1,000)' },
    { id: 'mid', label: 'Mid-Range ($1,000-$3,500)' },
    { id: 'luxury', label: 'Luxury ($3,500+)' }
];

const DAYS_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'short', label: '1-3 Days' },
    { id: 'medium', label: '4-7 Days' },
    { id: 'long', label: '8+ Days' }
];

const DEST_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'Serengeti', label: 'Serengeti' },
    { id: 'Zanzibar', label: 'Zanzibar' },
    { id: 'Kilimanjaro', label: 'Kilimanjaro' },
    { id: 'Cultural', label: 'Cultural' }
];

function ItineraryCard({ item, onClick }) {
    return (
        <div
            className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
            onClick={onClick}
            data-testid={`itinerary-card-${item.id}`}
        >
            <div className="relative aspect-[16/10] overflow-hidden">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 left-3 flex gap-2">
                    <span className="bg-white/90 backdrop-blur-sm text-[#073763] text-xs font-semibold px-3 py-1 rounded-full">{item.days} Days</span>
                    <span className="bg-[#073763]/90 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">{item.destination}</span>
                </div>
            </div>
            <div className="p-5">
                <h3 className="font-semibold text-[#1A1A1A] mb-2 group-hover:text-[#073763] transition-colors">{item.title}</h3>
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><Star size={14} className="text-yellow-500 fill-yellow-500" />{item.rating}</span>
                    <span>({item.reviews} reviews)</span>
                    <span className="flex items-center gap-1"><Users size={14} />{item.groupSize}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4">
                    {item.highlights?.slice(0, 3).map((h, i) => (
                        <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{h}</span>
                    ))}
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div>
                        <span className="text-xs text-gray-400">From</span>
                        <p className="text-lg font-bold text-[#073763]">${item.priceFrom?.toLocaleString()}</p>
                    </div>
                    <span className="text-sm text-[#073763] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        View Details <ArrowRight size={16} />
                    </span>
                </div>
            </div>
        </div>
    );
}

function DetailModal({ item, open, onClose }) {
    const navigate = useNavigate();
    const { isAuthenticated, api } = useAuth();
    const [showInquiry, setShowInquiry] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [inquiryForm, setInquiryForm] = useState({
        start_date: '', end_date: '', group_size: 2, interests: '', special_requests: ''
    });

    const handleInquirySubmit = async () => {
        if (!inquiryForm.start_date || !inquiryForm.group_size) return;
        setSubmitting(true);
        try {
            await api.post(`/itineraries/${item.sourceId || item.id}/inquire`, {
                travel_dates: { start: inquiryForm.start_date, end: inquiryForm.end_date || inquiryForm.start_date },
                group_size: parseInt(inquiryForm.group_size),
                interests: inquiryForm.interests ? inquiryForm.interests.split(',').map(s => s.trim()).filter(Boolean) : [],
                special_requests: inquiryForm.special_requests || null,
            });
            setShowInquiry(false);
            setInquiryForm({ start_date: '', end_date: '', group_size: 2, interests: '', special_requests: '' });
            onClose();
            alert('Your inquiry has been sent! The operator will receive your request.');
        } catch (e) {
            alert(e.response?.data?.detail || 'Failed to send inquiry. Please sign in as a traveler.');
        } finally { setSubmitting(false); }
    };

    if (!item) return null;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) { setShowInquiry(false); } onClose(); }}>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto p-0 gap-0">
                <DialogTitle className="sr-only">{item.title}</DialogTitle>
                <DialogDescription className="sr-only">Details for {item.title}</DialogDescription>
                <div className="relative h-56 sm:h-64">
                    <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-4 left-6 right-6">
                        <div className="flex gap-2 mb-2">
                            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">{item.days} Days</span>
                            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">{item.destination}</span>
                            <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">From ${item.priceFrom?.toLocaleString()}</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-white">{item.title}</h2>
                    </div>
                </div>
                <div className="p-6 space-y-6">
                    <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-yellow-600 font-medium"><Star size={16} className="fill-yellow-500 text-yellow-500" />{item.rating} ({item.reviews} reviews)</span>
                        <span className="flex items-center gap-1 text-gray-500"><Users size={16} /> Group: {item.groupSize}</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                    <div>
                        <h3 className="font-semibold text-[#1A1A1A] mb-3">Highlights</h3>
                        <div className="flex flex-wrap gap-2">
                            {item.highlights?.map((h, i) => (
                                <span key={i} className="text-sm bg-[#073763]/10 text-[#073763] px-3 py-1.5 rounded-full font-medium">{h}</span>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#1A1A1A] mb-3">Day-by-Day Itinerary</h3>
                        <div className="space-y-3">
                            {item.dailyPlan?.map((day) => (
                                <div key={day.day} className="flex gap-4 items-start">
                                    <div className="w-10 h-10 rounded-full bg-[#073763] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{day.day}</div>
                                    <div className="flex-1 pb-3 border-b border-gray-100 last:border-0">
                                        <p className="font-medium text-[#1A1A1A] text-sm">{day.title}</p>
                                        <p className="text-gray-500 text-sm mt-0.5">{day.activities}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h3 className="font-semibold text-[#1A1A1A] mb-3">What's Included</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {item.includes?.map((inc, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#073763]" />{inc}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Inquiry Form */}
                    {showInquiry && (
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 space-y-4" data-testid="inquiry-form">
                            <div className="flex items-center justify-between mb-1">
                                <h3 className="font-semibold text-[#1A1A1A]">Book This Experience</h3>
                                <button onClick={() => setShowInquiry(false)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-xs font-medium text-gray-500 mb-1 block">Start Date *</Label>
                                    <Input type="date" value={inquiryForm.start_date} onChange={e => setInquiryForm(p => ({ ...p, start_date: e.target.value }))}
                                        className="rounded-lg border-gray-200 h-10 text-sm" data-testid="inquiry-start-date" />
                                </div>
                                <div>
                                    <Label className="text-xs font-medium text-gray-500 mb-1 block">End Date</Label>
                                    <Input type="date" value={inquiryForm.end_date} onChange={e => setInquiryForm(p => ({ ...p, end_date: e.target.value }))}
                                        className="rounded-lg border-gray-200 h-10 text-sm" data-testid="inquiry-end-date" />
                                </div>
                            </div>
                            <div>
                                <Label className="text-xs font-medium text-gray-500 mb-1 block">Number of Travelers *</Label>
                                <Input type="number" min={1} max={30} value={inquiryForm.group_size} onChange={e => setInquiryForm(p => ({ ...p, group_size: e.target.value }))}
                                    className="rounded-lg border-gray-200 h-10 text-sm w-28" data-testid="inquiry-group-size" />
                            </div>
                            <div>
                                <Label className="text-xs font-medium text-gray-500 mb-1 block">Your Interests</Label>
                                <Input value={inquiryForm.interests} onChange={e => setInquiryForm(p => ({ ...p, interests: e.target.value }))}
                                    placeholder="e.g., Wildlife, Photography, Beach"
                                    className="rounded-lg border-gray-200 h-10 text-sm" data-testid="inquiry-interests" />
                            </div>
                            <div>
                                <Label className="text-xs font-medium text-gray-500 mb-1 block">Special Requests</Label>
                                <Input value={inquiryForm.special_requests} onChange={e => setInquiryForm(p => ({ ...p, special_requests: e.target.value }))}
                                    placeholder="Any dietary needs, accessibility, etc."
                                    className="rounded-lg border-gray-200 h-10 text-sm" data-testid="inquiry-special" />
                            </div>
                            <Button onClick={handleInquirySubmit} disabled={submitting || !inquiryForm.start_date || !inquiryForm.group_size}
                                className="w-full bg-[#073763] hover:bg-[#052a4a] text-white rounded-lg py-2.5 font-medium" data-testid="submit-inquiry-btn">
                                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}
                                {submitting ? 'Sending...' : 'Send Inquiry'}
                            </Button>
                        </div>
                    )}

                    <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div>
                            <p className="text-sm text-gray-500">Starting from</p>
                            <p className="text-2xl font-bold text-[#073763]">${item.priceFrom?.toLocaleString()} <span className="text-sm font-normal text-gray-400">/ person</span></p>
                        </div>
                        {!showInquiry && (
                            isAuthenticated ? (
                                <Button onClick={() => setShowInquiry(true)} className="btn-primary px-8 py-3 text-base" data-testid="book-now-btn">
                                    Book Now <ChevronRight className="ml-1" size={18} />
                                </Button>
                            ) : (
                                <Button onClick={() => navigate('/')} className="btn-primary px-8 py-3 text-base" data-testid="signin-to-book-btn">
                                    Sign In to Book <ChevronRight className="ml-1" size={18} />
                                </Button>
                            )
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function ExploreExperiences() {
    const [activeTab, setActiveTab] = useState('all');
    const [subFilter, setSubFilter] = useState('all');
    const [selectedItem, setSelectedItem] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [experiences, setExperiences] = useState([]);
    const [promotedItems, setPromotedItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [expRes, promoRes, pubRes] = await Promise.allSettled([
                    fetch(`${API_URL}/api/experiences`),
                    fetch(`${API_URL}/api/promoted-itineraries`),
                    fetch(`${API_URL}/api/published-itineraries`)
                ]);
                if (expRes.status === 'fulfilled' && expRes.value.ok) {
                    setExperiences(await expRes.value.json());
                }
                if (promoRes.status === 'fulfilled' && promoRes.value.ok) {
                    setPromotedItems(await promoRes.value.json());
                }
                if (pubRes.status === 'fulfilled' && pubRes.value.ok) {
                    const published = await pubRes.value.json();
                    const mapped = published.map(it => ({
                        id: `pub-${it.id}`,
                        sourceId: it.id,
                        title: it.title,
                        description: it.description,
                        days: it.duration_days,
                        priceFrom: it.total_price,
                        destination: it.daily_plan?.[0]?.accommodation || 'Tanzania',
                        highlights: it.highlights || it.inclusions?.slice(0, 4) || [],
                        dailyPlan: it.daily_plan?.map(d => ({ day: d.day, title: d.title || `Day ${d.day}`, activities: Array.isArray(d.activities) ? d.activities.join(', ') : d.activities })),
                        includes: it.inclusions || [],
                        rating: 4.8,
                        reviews: 0,
                        groupSize: '2-8',
                        image: `https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600`,
                        budget: it.total_price < 1000 ? 'budget' : it.total_price < 3500 ? 'mid' : 'luxury',
                        isPublished: true,
                        operatorName: it.company_name,
                    }));
                    setExperiences(prev => [...prev, ...mapped]);
                }
            } catch (e) { console.error('Failed to load:', e); }
            finally { setLoading(false); }
        };
        fetchData();
    }, []);

    const getSubFilters = () => {
        switch (activeTab) {
            case 'budget': return BUDGET_FILTERS;
            case 'days': return DAYS_FILTERS;
            case 'destination': return DEST_FILTERS;
            default: return [];
        }
    };

    const getFiltered = () => {
        let filtered = [...experiences];
        if (activeTab === 'budget' && subFilter !== 'all') {
            filtered = filtered.filter(i => i.budget === subFilter);
        }
        if (activeTab === 'days' && subFilter !== 'all') {
            if (subFilter === 'short') filtered = filtered.filter(i => i.days <= 3);
            else if (subFilter === 'medium') filtered = filtered.filter(i => i.days >= 4 && i.days <= 7);
            else if (subFilter === 'long') filtered = filtered.filter(i => i.days >= 8);
        }
        if (activeTab === 'destination' && subFilter !== 'all') {
            filtered = filtered.filter(i => i.destination === subFilter);
        }
        return filtered;
    };

    const handleTabChange = (tabId) => { setActiveTab(tabId); setSubFilter('all'); };
    const filtered = getFiltered();
    const getDashboardLink = () => {
        if (!user) return '/';
        switch (user.role) { case 'admin': return '/admin'; case 'operator': return '/operator'; default: return '/dashboard'; }
    };

    return (
        <div className="min-h-screen bg-white">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
                <div className="flex items-center justify-between">
                    <div className="px-6 py-4"><Link to="/" className="text-2xl font-bold text-[#073763]">safarinodes</Link></div>
                    <div className="hidden md:flex items-center gap-8">
                        <Link to="/destinations" className="font-medium text-gray-700 hover:text-[#073763] transition-colors">Discover Tanzania</Link>
                        <Link to="/experiences" className="font-medium text-[#073763] transition-colors">Explore Experiences</Link>
                    </div>
                    <div className="bg-[#073763] px-8 py-3 mr-4 my-2 rounded-full">
                        {isAuthenticated ? (
                            <button onClick={() => navigate(getDashboardLink())} className="text-white font-medium hover:text-white/80 transition-colors">Dashboard</button>
                        ) : (
                            <Link to="/" className="text-white font-medium hover:text-white/80 transition-colors">Sign in</Link>
                        )}
                    </div>
                </div>
            </nav>

            <section className="pt-28 pb-8 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">
                    <p className="text-[#073763] font-medium mb-2 uppercase tracking-wide text-sm">Curated Itineraries</p>
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-3" data-testid="experiences-title">Explore Experiences</h1>
                    <p className="text-gray-500 max-w-2xl">Browse our handpicked itineraries crafted by verified local operators. Filter by budget, duration, or destination.</p>
                </div>
            </section>

            <div className="sticky top-[64px] z-40 bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex gap-1 overflow-x-auto py-3" data-testid="filter-tabs">
                        {FILTER_TABS.map(tab => (
                            <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                                    activeTab === tab.id ? 'bg-[#073763] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`} data-testid={`filter-tab-${tab.id}`}>
                                {tab.icon && <tab.icon size={16} />}{tab.label}
                            </button>
                        ))}
                    </div>
                    {activeTab !== 'all' && (
                        <div className="flex gap-2 pb-3 overflow-x-auto" data-testid="sub-filters">
                            {getSubFilters().map(f => (
                                <button key={f.id} onClick={() => setSubFilter(f.id)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
                                        subFilter === f.id ? 'border-[#073763] bg-[#073763]/10 text-[#073763]' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                                    }`} data-testid={`sub-filter-${f.id}`}>{f.label}</button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <section className="py-10">
                <div className="max-w-7xl mx-auto px-6">
                    {loading ? (
                        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#073763]" size={36} /></div>
                    ) : (
                        <>
                            <p className="text-sm text-gray-400 mb-6" data-testid="results-count">{filtered.length} experience{filtered.length !== 1 ? 's' : ''} found</p>

                            {/* Featured/Promoted Itineraries */}
                            {promotedItems.length > 0 && activeTab === 'all' && (
                                <div className="mb-10">
                                    <div className="flex items-center gap-2 mb-4">
                                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                                        <h2 className="text-sm font-semibold text-amber-700 uppercase tracking-wide">Featured by Operators</h2>
                                    </div>
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {promotedItems.map(promo => (
                                            <div key={promo.id} className="group bg-white rounded-xl overflow-hidden border-2 border-amber-200 shadow-sm hover:shadow-lg transition-all duration-300" data-testid={`promoted-${promo.id}`}>
                                                <div className="bg-amber-50 px-4 py-2 flex items-center justify-between">
                                                    <span className="text-xs font-semibold text-amber-700 flex items-center gap-1"><Star size={12} className="fill-amber-500 text-amber-500" /> Featured</span>
                                                    <span className="text-[10px] text-amber-500">{promo.company_name}</span>
                                                </div>
                                                <div className="p-5">
                                                    <h3 className="font-semibold text-[#1A1A1A] mb-2">{promo.itinerary_title}</h3>
                                                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">{promo.description}</p>
                                                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                                                        <span className="flex items-center gap-1"><Clock size={14} />{promo.duration_days} days</span>
                                                        {promo.inclusions?.length > 0 && <span>{promo.inclusions.length} inclusions</span>}
                                                    </div>
                                                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                                                        <p className="text-lg font-bold text-[#073763]">${promo.total_price?.toLocaleString()}</p>
                                                        <Button onClick={() => { setSelectedItem({ id: `pub-${promo.itinerary_id}`, sourceId: promo.itinerary_id, title: promo.itinerary_title, description: promo.description, days: promo.duration_days, priceFrom: promo.total_price, destination: 'Tanzania', highlights: [], dailyPlan: [], includes: promo.inclusions || [], rating: 4.8, reviews: 0, groupSize: '2-8', image: `https://images.unsplash.com/photo-1516426122078-c23e76319801?w=600`, operatorName: promo.company_name }); setShowDetail(true); }} className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg px-4 py-2 text-sm font-medium" data-testid={`inquire-promoted-${promo.id}`}>
                                                            Book Now
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {filtered.length === 0 ? (
                                <div className="text-center py-20">
                                    <Compass size={48} className="mx-auto text-gray-300 mb-4" />
                                    <p className="text-gray-500 text-lg">No experiences match your filters.</p>
                                    <button onClick={() => { setActiveTab('all'); setSubFilter('all'); }} className="text-[#073763] font-medium mt-2 hover:underline">Clear filters</button>
                                </div>
                            ) : (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {filtered.map(item => (
                                        <ItineraryCard key={item.id} item={item} onClick={() => { setSelectedItem(item); setShowDetail(true); }} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            <section className="py-16 bg-[#073763]">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">Can't find what you're looking for?</h2>
                    <p className="text-white/70 mb-8">Create a custom trip snapshot and receive personalized itineraries from our verified operators.</p>
                    <Button onClick={() => navigate('/trip/create')} className="bg-white text-[#073763] hover:bg-gray-100 rounded-full px-8 py-3 font-semibold text-base" data-testid="custom-trip-btn">
                        Create Custom Trip <ChevronRight className="ml-1" size={18} />
                    </Button>
                </div>
            </section>

            <DetailModal item={selectedItem} open={showDetail} onClose={() => setShowDetail(false)} />
        </div>
    );
}
